import React, { ReactNode, useMemo, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { Chip, ChipProps, Icon, formatNumber } from '@column/column-ui-kit';

import { CopyInput } from '~/components';
import { CheckScannerIcon } from '~/elements/icons/transfers/CheckScanner';
import { Flex, FormElement, FormLabel, Inner, Text } from '~/styles';
import { ACHTransferBatch } from '~/typings/API';
import { formatDate, formatDateTime } from '~/util';

const Summary = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 24px;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const Meta = styled.ul`
  display: flex;
  align-items: center;
  gap: 6px 16px;
  list-style: none;
  flex-wrap: wrap;
  margin: 0;
  padding: 0;

  li {
    display: flex;
    align-items: center;
  }
`;

const IdField = styled.div`
  min-width: 310px;
  max-width: 25%;

  @media (max-width: 900px) {
    min-width: 0;
    max-width: 100%;
  }
`;

const DateTimeDisplay = styled.div`
  position: relative;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const DateTimeLocal = styled.span`
  transition: opacity 0.2s ease-in-out;
  opacity: 1;
  position: absolute;
  left: 0;
`;

const DateTimeUTC = styled(DateTimeLocal)`
  opacity: 0;
  position: relative;
`;

const DayLocal = styled.span`
  position: absolute;
  top: 2px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 600;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  transition: opacity 0.2s ease-in-out;
`;

const DayUTC = styled(DayLocal)`
  opacity: 0;
`;

const DayDisplay = styled.div`
  position: relative;

  .icon-calendar-line {
    display: none;
  }
`;

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};

  &:hover {
    ${DateTimeLocal},
    ${DayLocal} {
      opacity: 0;
    }

    ${DateTimeUTC},
    ${DayUTC} {
      opacity: 1;
    }
  }
`;

const Balance = styled.div`
  display: grid;
  grid-template-columns: auto;
  grid-gap: 16px;

  @media (min-width: 400px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-gap: 88px;
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const SpinIcon = styled.span<{ $spin?: boolean }>`
  display: inline-flex;
  ${({ $spin }) =>
    $spin &&
    css`
      animation: ${spin} 1.2s linear infinite;
    `}
`;

const IN_PROGRESS_STATUSES = new Set(['validating', 'processing']);

const VALIDATED_BREAKDOWN_STATUSES = new Set(['validated', 'processing']);
const CREATED_BREAKDOWN_STATUSES = new Set(['completed', 'failed']);

const buildBreakdownText = (batch: ACHTransferBatch): string => {
  const validated = batch.totalCount - batch.validationFailedCount;
  let parts: { label: string; count: number }[] = [];
  if (VALIDATED_BREAKDOWN_STATUSES.has(batch.status)) {
    parts = [
      { label: 'Validated', count: validated },
      { label: 'Invalid', count: batch.validationFailedCount },
    ];
  } else if (CREATED_BREAKDOWN_STATUSES.has(batch.status)) {
    parts = [
      { label: 'Created', count: batch.succeededCount },
      { label: 'Failed', count: batch.failedCount },
      { label: 'Invalid', count: batch.validationFailedCount },
    ];
  }
  return parts
    .filter((p) => p.count > 0)
    .map((p) => `${p.count} ${p.label}`)
    .join(' · ');
};

const RecordsChipText: React.FC<{ totalText: string; breakdownText: string }> = ({ totalText, breakdownText }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <span onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      {hovered && breakdownText ? breakdownText : totalText}
    </span>
  );
};

type StatusMeta = { type: ChipProps['type']; label: string; icon: ReactNode };

const STATUS_META: Record<string, StatusMeta> = {
  pending: { type: 'default', label: 'Pending', icon: <Icon.CircleBlank /> },
  validating: { type: 'info', label: 'Validating', icon: <Icon.CirclePendingOne /> },
  validated: { type: 'info', label: 'Validated', icon: <Icon.CirclePendingTwo /> },
  processing: { type: 'info', label: 'Processing', icon: <Icon.CirclePendingThree /> },
  completed: { type: 'success', label: 'Completed', icon: <Icon.CircleCheck /> },
  failed: { type: 'danger', label: 'Failed', icon: <Icon.CircleCross /> },
  expired: { type: 'danger', label: 'Expired', icon: <Icon.CircleCross /> },
  canceled: { type: 'danger', label: 'Canceled', icon: <Icon.CircleCross /> },
};

const DEFAULT_STATUS: StatusMeta = { type: 'default', label: '', icon: <Icon.CircleBlank /> };

const dayNumber = (iso: string, timeZoneFormat: 'local' | 'utc') =>
  formatDate(iso, { dateFormat: 'short', timeZoneFormat }).split(' ')[1].replace(',', '');

const amountColor = (loaded: boolean, amount: number, highlightPositive = false) =>
  !loaded ? 'lighter' : highlightPositive && amount > 0 ? 'success' : 'foreground';

interface AchBatchHeaderProps {
  batch: ACHTransferBatch;
}

export const AchBatchHeader: React.FC<AchBatchHeaderProps> = ({ batch }) => {
  const recordLabel = batch.totalCount === 1 ? 'Record' : 'Records';
  const breakdownText = useMemo(() => buildBreakdownText(batch), [batch]);

  const hasValidated = !!batch.validatedAt;
  const hasProcessed = !!batch.processingAt;

  const status = STATUS_META[batch.status] ?? { ...DEFAULT_STATUS, label: batch.status };

  return (
    <Inner px="var(--page-margin)">
      <Summary>
        <Flex flexDirection="column" gap={12}>
          <Balance>
            <Stat
              label="Validated Credit"
              value={hasValidated ? formatNumber(batch.validatedTotalCreditAmount, 'USD') : '—'}
              color={amountColor(hasValidated, batch.validatedTotalCreditAmount, true)}
            />
            <Stat
              label="Validated Debit"
              value={hasValidated ? formatNumber(batch.validatedTotalDebitAmount, 'USD') : '—'}
              color={amountColor(hasValidated, batch.validatedTotalDebitAmount)}
            />
            <Stat
              label="Created Credit"
              value={hasProcessed ? formatNumber(batch.totalCreditAmount, 'USD') : '—'}
              color={amountColor(hasProcessed, batch.totalCreditAmount, true)}
            />
            <Stat
              label="Created Debit"
              value={hasProcessed ? formatNumber(batch.totalDebitAmount, 'USD') : '—'}
              color={amountColor(hasProcessed, batch.totalDebitAmount)}
            />
          </Balance>

          <Meta>
            <li>
              <Chip
                variant="subtle"
                type={status.type}
                icon={<SpinIcon $spin={IN_PROGRESS_STATUSES.has(batch.status)}>{status.icon}</SpinIcon>}
                size="large"
              >
                {status.label}
              </Chip>
            </li>
            <li>
              <Chip icon={<CheckScannerIcon />} size="large" variant="subtle">
                <RecordsChipText totalText={`${batch.totalCount} ${recordLabel}`} breakdownText={breakdownText} />
              </Chip>
            </li>
            <li>
              <DateRow>
                <DayDisplay>
                  <DayLocal>{dayNumber(batch.createdAt, 'local')}</DayLocal>
                  <DayUTC>{dayNumber(batch.createdAt, 'utc')}</DayUTC>
                  <Icon.Calendar />
                </DayDisplay>
                <DateTimeDisplay>
                  <DateTimeLocal>
                    <Text size={14} color="currentColor" weight="medium" whiteSpace="nowrap">
                      {formatDateTime(batch.createdAt, { timeZoneFormat: 'local' })}
                    </Text>
                  </DateTimeLocal>
                  <DateTimeUTC>
                    <Text size={14} color="currentColor" weight="medium" whiteSpace="nowrap">
                      {formatDateTime(batch.createdAt, { timeZoneFormat: 'utc' })}
                    </Text>
                  </DateTimeUTC>
                </DateTimeDisplay>
              </DateRow>
            </li>
          </Meta>
        </Flex>
        <IdField>
          <FormElement>
            <FormLabel>Batch ID</FormLabel>
            <CopyInput value={batch.id} size="small" isMonospaced />
          </FormElement>
        </IdField>
      </Summary>
    </Inner>
  );
};

const Stat: React.FC<{ label: string; value: string; color?: 'foreground' | 'success' | 'lighter' }> = ({
  label,
  value,
  color = 'foreground',
}) => (
  <Flex flexDirection="column">
    <Text size={20} lineHeight="28px" weight="semibold" color={color} letterSpacing="tight">
      {value}
    </Text>
    <Text size={12} color="light" weight="normal" whiteSpace="nowrap">
      {label}
    </Text>
  </Flex>
);
